import { z } from 'zod';

/** The 8 fixed columns. Global defaults, seeded once, not per-company. */
export const PALIER_NUMBERS = [-10, 0, 1, 2, 3, 4, 5, 6] as const;

/**
 * Per-palier header colour — decision F5 (2026-08-25 doc).
 *
 * Exists because the export renderer needs **literal hex**: the backend prints
 * its HTML through Playwright, which never loads the frontend's `styles.css`,
 * so a `var(--color-palier-…)` reference would resolve to nothing and every
 * header would come out unpainted. The `@theme` custom properties remain the
 * app's own source (Tailwind generates `bg-palier-*` from them); this map is
 * the renderer's.
 *
 * Two places therefore hold the same eight colours. `cssVar` is carried
 * alongside each hex so `palier-palette-drift.test.ts` can parse `styles.css`
 * and assert the two agree — divergence fails CI instead of silently shipping
 * an export whose headers do not match the grid the user was just looking at.
 * Codegen was considered and rejected as heavy machinery for eight colours.
 *
 * Follows the `NOYAU_COLOR_TOKEN` precedent in `noyau.ts` — presentation
 * bridges for a fixed business enum live beside that enum.
 *
 * Palier -10 is `gray-200` (issue #16, planche 06), the grey the
 * `noyau-gradient` theme already gives it, so both themes read sabotage as
 * off-scale. Noir on it is 14.50:1.
 */
export const PALIER_HEADER_THEME: Record<number, { cssVar: string; hex: string }> = {
  [-10]: { cssVar: '--color-gray-200', hex: '#d9d6d2' },
  0: { cssVar: '--color-palier-attentes-0', hex: '#a8b2c0' },
  1: { cssVar: '--color-palier-attentes-1', hex: '#96a1b2' },
  2: { cssVar: '--color-palier-attentes-2', hex: '#8390a4' },
  3: { cssVar: '--color-palier-attentes-3', hex: '#717e93' },
  4: { cssVar: '--color-palier-singularite-4', hex: '#e0b563' },
  5: { cssVar: '--color-palier-singularite-5', hex: '#d9a94a' },
  6: { cssVar: '--color-palier-singularite-6', hex: '#c89638' },
};

/**
 * The `matrix-header-theme` tweak's values, as they travel on the wire
 * (decision F3). `white` is the flat white/grey header; `pitch` paints each
 * palier its `PALIER_HEADER_THEME` colour; `noyau-gradient` ramps from white
 * to the company's own noyau colour (`palierRampStep`).
 *
 * **These are the tweak's values verbatim, and must stay that way.** The page
 * reads the tweak and puts it straight on the wire, so any gap between the two
 * lists is a 400 on every PDF and HTML export — which is exactly what issue #8
 * was: this enum kept the pre-`ec4eed9` names (`current`, `palier-colore`)
 * after the tweak renamed them, and a cast on the page hid it from the
 * typechecker. `export-header-theme-contract.test.ts` now fails if they drift
 * again.
 */
export const exportHeaderThemeSchema = z.enum(['white', 'pitch', 'noyau-gradient']);
export type ExportHeaderTheme = z.infer<typeof exportHeaderThemeSchema>;

export const palierNumberSchema = z.union([
  z.literal(-10),
  z.literal(0),
  z.literal(1),
  z.literal(2),
  z.literal(3),
  z.literal(4),
  z.literal(5),
  z.literal(6),
]);
export type PalierNumber = z.infer<typeof palierNumberSchema>;

/**
 * Where a palier sits among the 8 columns, from 1 to 8 (issue #28): -10 is 1,
 * 0 is 2, 6 is 8. The number is the storage key and nothing else; the model
 * and the user both read this position, so a comment that says « palier 3 »
 * means what the model was shown as « Palier 3 sur 8 ». It ignores what is
 * generated, hidden or selected.
 */
export function palierPosition(number: PalierNumber): number {
  return PALIER_NUMBERS.indexOf(number) + 1;
}

/** The palier number at `position` (1 to 8), or undefined for any other value. */
export function palierNumberAtPosition(position: number): PalierNumber | undefined {
  return Number.isInteger(position) ? PALIER_NUMBERS[position - 1] : undefined;
}

/**
 * Ceiling on a palier description (issue #1).
 *
 * A palier description is a paragraph of doctrine, not a document: it is
 * injected verbatim into every generation prompt's `# Paliers` block, eight
 * times over, so an unbounded field would quietly eat the context budget the
 * digests were built to protect. Deliberately generous compared to the 200
 * characters a label gets — prose needs room — and deliberately far below
 * `DIGEST_MAX_CHARS`.
 */
export const PALIER_DESCRIPTION_MAX_CHARS = 2_000;

export const palierDefaultSchema = z.object({
  number: palierNumberSchema,
  defaultLabel: z.string().min(1).max(200),
  /**
   * Seeded empty and left NULL for all 8 paliers — an explicit product
   * decision, not an oversight: the global doctrine text is not settled, and
   * inventing one here would put words in the model's mouth on every matrix of
   * every company. The column exists so a default *can* be filled in later
   * without another migration.
   */
  defaultDescription: z.string().max(PALIER_DESCRIPTION_MAX_CHARS).nullable(),
});
export type PalierDefault = z.infer<typeof palierDefaultSchema>;

/**
 * One level's override of one palier — the same shape at company level
 * (`company_paliers`) and at matrix level (`matrix_paliers`).
 *
 * Each field is independently nullable, and null means "inherit", never
 * "blank". That is what lets a matrix override only a label while still
 * inheriting its company's description — see `getResolvedPaliers`.
 */
export const palierOverrideSchema = z.object({
  number: palierNumberSchema,
  customLabel: z.string().max(200).nullable(),
  customDescription: z.string().max(PALIER_DESCRIPTION_MAX_CHARS).nullable(),
});
export type PalierOverride = z.infer<typeof palierOverrideSchema>;

/** The company level of {@link palierOverrideSchema}, under the name the company routes have always used. */
export const companyPalierSchema = palierOverrideSchema;
export type CompanyPalier = PalierOverride;

/**
 * Composed view every surface actually renders: defaults, then the company's
 * overrides, then the matrix's, resolved field by field.
 *
 * `description` is null far more often than not — the defaults ship empty and
 * most paliers are never described — so every consumer must treat absence as
 * the ordinary case, not as missing data.
 */
export const resolvedPalierSchema = z.object({
  number: palierNumberSchema,
  label: z.string().min(1).max(200),
  description: z.string().max(PALIER_DESCRIPTION_MAX_CHARS).nullable(),
});
export type ResolvedPalier = z.infer<typeof resolvedPalierSchema>;

/**
 * `PUT /companies/:id/paliers` and `PUT /matrices/:id/paliers` — any subset of
 * the 8 numbers; each present entry is upserted whole (both fields). A number
 * left out is untouched (not reset to default).
 */
export const updatePaliersInputSchema = z.array(palierOverrideSchema);
export type UpdatePaliersInput = z.infer<typeof updatePaliersInputSchema>;
